import { useRouter } from "next/router";
import { useEffect, useRef, useState } from "react";
import { useUI } from "@context/ui-managed-context";
import auth from "@api/auth";
import { useAppContext } from "@context/app";
import { getCookie } from "@utils/Cookies";
import {
  COOKIE_ACCESS_TOKEN_KEY,
  COOKIE_FRONT_TOKEN_KEY,
  COOKIE_REFRESH_TOKEN_KEY,
} from "@utils/constants";
import useRequestImmutable from "@shared/hooks/useRequestImmutable";
import { useIsMount } from "@utils/helpers";
import { useSWRConfig } from "swr";
import { modalActions } from "@context/modals";
import { useIsMobile } from "@shared/hooks/useIsMobile";
import { useGoftino } from "@shared/components/chat-setting/useGoftino";
import { useLayoutContext } from "@context/layout";
import orderApi from "@api/order";
import { MODAL_TYPES } from "types/modals";
import userApi from "../helpers/userApi";

const UseCommonProfile = (
  defaultSelected?: string | undefined,
  revalidate?: boolean | undefined
) => {
  const { showFestivalBanner, setUserLoggedIn, setLayoutLoaded } =
    useAppContext();
  const Cookie = getCookie();
  const { refetchUser, userData: layoutUserData } = useLayoutContext();

  const router = useRouter();
  const contentRef = useRef<HTMLDivElement | null>(null);
  const [shoMobileModal, setShowMobileModal] = useState(false);
  const [selected, setSelected] = useState(defaultSelected);
  const [showBanner, setShowBanner] = useState(showFestivalBanner);

  const [loading, setLoading] = useState(false);
  const isMount = useIsMount();
  const isMobile = useIsMobile(905) || false;
  const [notify, setNotify] = useState({
    show: false,
    message: "",
    success: false,
  });
  const { hideGoftino } = useGoftino();
  const { cache, mutate } = useSWRConfig();
  useEffect(() => {
    if (!showFestivalBanner) setShowBanner(showFestivalBanner);
  }, [showFestivalBanner]);
  const { openAlert, setAlertView } = useUI();

  const {
    data: { data: userData = undefined } = { data: undefined },
    mutate: mutateProfile,
  } = useRequestImmutable(userApi.getProfile(false));
  const { data: permanentCoupon } = useRequestImmutable(
    orderApi.permentCoupon()
  );
  const logoutConfirmation = async () => {
    try {
      const res = await auth.logout().fetch();
      refetchUser?.();
      if (res.status) {
        Cookie.remove(COOKIE_ACCESS_TOKEN_KEY);
        Cookie.remove(COOKIE_REFRESH_TOKEN_KEY);
        Cookie.remove(COOKIE_FRONT_TOKEN_KEY);
        setUserLoggedIn(false);
        setLayoutLoaded(false);
        if (cache instanceof Map) {
          const keys: string[] = Array.from(cache.keys());
          cache.clear();
          await Promise.all(keys.map((key) => mutate(key)));
        }
      }
    } catch (error: any) {
      setAlertView("ALERT", [
        {
          mode: "danger",
          textBody: error.message || error.error.message || "دوباره تلاش کنید.",
        },
      ]);
      openAlert();
    } finally {
      modalActions.clearModals();
      if (!layoutUserData?.is_login) {
        setLayoutLoaded(false);
        router.push("/register");
      }
    }
  };

  const logout = () => {
    modalActions.addModal(MODAL_TYPES.LOGOUT_CONFIRMATION, {
      config: {
        hideCloseIcon: true,
        modalClassName: "rounded-t-24 rounded-b-16",
      },
      data: {
        logout: logoutConfirmation,
      },
    });
  };

  useEffect(() => {
    if (typeof window !== undefined) {
      window.addEventListener("scroll", () => {
        if (window.scrollY > 500) {
          setShowBanner(false);
        } else {
          if (showFestivalBanner) setShowBanner(true);
        }
      });
    }
    hideGoftino();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const verifyEmail = async () => {
    setLoading(true);
    if (!userData) {
      setNotify({
        show: true,
        message: "لطفا ابتدا ایمیل خود را ثبت کنید.",
        success: false,
      });
      return;
    }
    if (userData && userData.user.email) {
      return userApi
        .verifyMobileEmail({ type: "email", value: userData.user.email || "" })
        .fetch()
        .then((data) => {
          setNotify({
            show: true,
            message: data.message,
            success: data.status,
          });
          setLoading(false);
          return data;
        })
        .catch((error) => {
          setNotify({
            show: true,
            message: error.error.message,
            success: false,
          });
          setLoading(false);
          return error;
        });
    }
  };

  const verifyMobile = () => {
    localStorage.setItem("showMobile", "1");
    window.dispatchEvent(new Event("storage"));
    router.push("/my-account/change-profile");
  };

  // scroll to top of 'contend div' after each time route changes
  useEffect(() => {
    const element = contentRef.current as HTMLElement;
    if (router.asPath !== "/my-account") {
      window.scrollTo({
        top: element?.offsetTop - 150,
        behavior: "smooth",
      });
    }
  }, [router.reload]);

  useEffect(() => {
    if (!isMount) {
      mutateProfile();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [revalidate]);
  useEffect(() => {
    //check notify for email or mobile confirmation
    if (notify.show && !notify.success) {
      setTimeout(() => {
        setNotify({
          show: false,
          message: "",
          success: true,
        });
      }, 5000);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notify.show]);

  const { setHideTopbar, setHideFooter } = useAppContext();
  useEffect(() => {
    setHideTopbar(isMobile);
    setHideFooter(isMobile);
    return () => {
      setHideTopbar(false);
      setHideFooter(false);
    };
  }, [isMobile]);

  return {
    verifyEmail,
    verifyMobile,
    userData,
    logout,
    loading,
    selected,
    setSelected,
    showFestivalBanner,
    showBanner,
    notify,
    contentRef,
    shoMobileModal,
    setShowMobileModal,
    permanentCoupon,
    layoutUserData,
  };
};

export default UseCommonProfile;
